import { useCallback, useState } from 'react';
import { Typography, Select, Button, Card, Space, Tag, Empty, message } from 'antd';
import { SplitCellsOutlined, SwapOutlined, LinkOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useApi } from '../../hooks/use_api';
import './SideBySidePage.css';

const { Title, Text, Paragraph } = Typography;

// One picker: debounced hybrid search (same call/shape as Homepage.jsx's
// search bar) feeding an antd Select, since this needs to find *any* listed
// entity, not just ones the current user administers (ConnectedEntities.jsx's
// static-preload picker doesn't fit here).
function EntityPicker({ label, value, onChange, api }) {
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);

    const search = useCallback((query) => {
        if (!query || query.trim().length < 2) { setOptions([]); return; }
        setSearching(true);
        api.post('/api/entities/search', { query, limit: 12, vector_weight: 0.5, text_weight: 0.5 })
            .then(results => setOptions(results.map(r => ({ value: r.id, label: `${r.name} [${r.entity_type}]` }))))
            .catch(() => setOptions([]))
            .finally(() => setSearching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(debounce(search, 400), [search]);

    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
            <Select
                showSearch
                filterOption={false}
                style={{ width: '100%', marginTop: 4 }}
                placeholder="Search entities…"
                value={value}
                onChange={onChange}
                onSearch={debouncedSearch}
                loading={searching}
                options={options}
                allowClear
                notFoundContent={searching ? 'Searching…' : 'Type to search'}
            />
        </div>
    );
}

function Profile({ side }) {
    const [first, ...rest] = side.descriptions;
    return (
        <Card className="side-profile" size="small" title={<span>{side.name} <Tag>{side.entity_type}</Tag></span>}>
            {!first && <Text type="secondary">No description yet.</Text>}
            {first && <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{first.content || <Text type="secondary">Empty</Text>}</Paragraph>}
            {rest.map(d => (
                <div key={d.id} className="side-extra">
                    <Text type="secondary" style={{ fontSize: 12 }}>{d.label}</Text>
                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{d.content}</Paragraph>
                </div>
            ))}
        </Card>
    );
}

function Shows({ name, shows }) {
    return (
        <div className="side-shows">
            <Text type="secondary" style={{ fontSize: 12 }}>{name} shows</Text>
            <div>{shows ? <><Tag>{shows.label}</Tag>{shows.content}</> : <Text type="secondary">their default description</Text>}</div>
        </div>
    );
}

function SideBySidePage() {
    const api = useApi();
    const [entityAId, setEntityAId] = useState(null);
    const [entityBId, setEntityBId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const canCompare = entityAId != null && entityBId != null && entityAId !== entityBId;

    const handleCompare = () => {
        if (!canCompare) return;
        setLoading(true);
        setResult(null);
        api.get(`/api/entities/${entityAId}/side-by-side/${entityBId}`)
            .then(setResult)
            .catch(err => message.error(err.status === 404 ? 'One of these is outside what you can see' : 'Could not load these entities'))
            .finally(() => setLoading(false));
    };

    return (
        <div className="side-page">
            <Space align="center" style={{ marginBottom: 4 }}>
                <SplitCellsOutlined style={{ fontSize: 20, color: 'var(--vellby-sky-500)' }} />
                <Title level={4} style={{ margin: 0 }}>Side-by-siding</Title>
            </Space>
            <Paragraph type="secondary" className="side-lede">
                Pick two entities to read their profiles next to each other, with every
                place they share and how each one shows itself there. To have it
                compared for you, ask your own agent: the same view is a tool on this
                node's MCP server.
            </Paragraph>

            <div className="side-pickers">
                <EntityPicker label="Entity A" value={entityAId} onChange={setEntityAId} api={api} />
                <SwapOutlined className="side-swap-icon" />
                <EntityPicker label="Entity B" value={entityBId} onChange={setEntityBId} api={api} />
            </div>

            <Button
                type="primary"
                onClick={handleCompare}
                disabled={!canCompare}
                loading={loading}
                style={{ marginTop: 16 }}
            >
                Put side by side
            </Button>

            {result && (
                <div className="side-result">
                    <div className="side-columns">
                        <Profile side={result.a} />
                        <Profile side={result.b} />
                    </div>
                    {result.direct && (
                        <Paragraph style={{ marginTop: 16 }}><LinkOutlined /> {result.a.name} and {result.b.name} are directly connected.</Paragraph>
                    )}
                    <Title level={5} style={{ marginTop: 24 }}>Where both belong</Title>
                    {result.shared.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shared contexts or relationships you can see" />}
                    {result.shared.map(place => (
                        <Card key={place.entity.id} size="small" className="side-place" title={<span>{place.entity.name} <Tag>{place.entity.entity_type}</Tag></span>}>
                            <div className="side-columns">
                                <Shows name={result.a.name} shows={place.a_shows} />
                                <Shows name={result.b.name} shows={place.b_shows} />
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SideBySidePage;
